<!-- This is a shared template for both Tender Details and Practice Details -->
<!-- Both components reference this file to avoid duplication -->
<!-- Differences are handled via component properties and conditional rendering -->

<div class="main">
    <div class="container-fluid">
        <!-- Header Section -->
        <div class="practices-header d-flex flex-wrap justify-content-between align-items-center m-4">
            <div class="practices-title-container mb-3 mb-md-0">
                <button class="btn back-btn p-0 mb-2 d-flex align-items-center" (click)="goBack()"
                    aria-label="Go back">
                    <i class="fas fa-arrow-left me-2"></i>
                    Go back to {{ isPractice ? 'Practices' : 'Tenders' }}
                </button>
                <div class="d-flex flex-wrap align-items-center gap-2 gap-md-3 mb-2">
                    <h3 class="mb-0">{{ tenderDetails?.tenderId || tenderId || (isPractice ? 'Practice' : 'Tender') }}</h3>
                    <app-status-badge
                        *ngIf="tenderStatus"
                        [text]="tenderStatus"
                        [color]="getStatusColor(tenderStatus)"
                        [size]="'medium'">
                    </app-status-badge>
                </div>
                <span>
                    <a [routerLink]="['/' + AppConstants.ROUTES.HOME]"
                        class="practices-header-title breadcrumb-link">Dashboard <i class="lni lni-chevron-right"
                            aria-hidden="true"></i></a>
                    <a [routerLink]="['/' + (isPractice ? AppConstants.ROUTES.PRACTICE : AppConstants.ROUTES.TENDER)]"
                        class="practices-header-title breadcrumb-link">{{ isPractice ? 'Practice' : 'Tender' }} List <i class="lni lni-chevron-right"
                            aria-hidden="true"></i></a>
                    <span> View {{ isPractice ? 'Practice' : 'Tender' }} Details</span>
                </span>
            </div>
            <div class="d-flex align-items-center gap-3">
                <button class="export-btn rounded-pill text-white" (click)="exportData()" aria-label="Export data" *ngIf="activeTab === 'Workflow'">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M10 2.5V13.75M10 2.5L6.25 6.25M10 2.5L13.75 6.25" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.5 13.75V16.25C2.5 17.0784 3.17157 17.75 4 17.75H16C16.8284 17.75 17.5 17.0784 17.5 16.25V13.75" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Export
                </button>
            </div>
        </div>

        <!-- Action Buttons Row -->
        <div class="action-buttons-top m-4">
            <div class="d-flex gap-2 flex-wrap">
                <button *ngIf="!isEditMode && canEditTender" class="btn action-btn-white" (click)="editTender()">Edit</button>
                <button *ngIf="isEditMode" class="btn action-btn-purple" (click)="updateTender()">Save</button>
                <button *ngIf="isEditMode" class="btn action-btn-white" (click)="cancelEdit()">Cancel</button>
                <button *ngIf="!isEditMode" class="btn action-btn-white" (click)="assignEvaluators()">Assign Evaluators</button>
                <!-- Start Voting button - only for Tenders, not Practices -->
                <!-- <button *ngIf="!isEditMode && !isPractice" class="btn action-btn-purple" (click)="startVoting()">Start Voting</button> -->
            </div>
        </div>

        <!-- Navigation Tabs -->
        <div class="tabs-navigation m-4">
            <div class="tabs-container">
                <button *ngFor="let tab of tabs" class="tab-item" [class.active]="activeTab === tab"
                    (click)="onTabChange(tab)">
                    {{ tab }}
                </button>
            </div>
        </div>

        <!-- Tender/Practice Tab Content -->
        <div class="mandatory-application m-4" *ngIf="activeTab === (isPractice ? 'Practice' : 'Tender')">
            <div class="inside-out p-4">
                <div class="mandatory-fields"></div>
                <form [formGroup]="tenderForm">
                    <div class="container pt-4">
                        <!-- General Section -->
                        <fieldset class="content-fieldset mb-4">
                            <legend class="section-legend">General</legend>

                            <div class="row mb-4">
                                <!-- Left Column -->
                                <div class="col-12 col-md-4">
                                    <div class="mb-3">
                                        <label for="tenderNumberLine" class="form-label fw-bold">Tender Number</label>
                                        <input type="text" id="tenderNumberLine"
                                            class="form-control rounded-4 bg-light p-2" formControlName="tenderNumber"
                                            readonly>
                                    </div>

                                    <div class="mb-3">
                                        <label for="tenderNameLine" class="form-label fw-bold">Tender name</label>
                                        <input type="text" id="tenderNameLine"
                                            class="form-control rounded-4 bg-light p-2" formControlName="tenderName"
                                            [readonly]="!isEditMode">
                                    </div>

                                    <app-searchable-select
                                        [options]="tenderTypes"
                                        [label]="'Tender Type'"
                                        [id]="'tenderTypeLine'"
                                        [placeholder]="'Search or select type...'"
                                        [disabled]="!isEditMode"
                                        formControlName="tenderType">
                                    </app-searchable-select>

                                    <app-searchable-select
                                        [options]="rfpRfqOptions"
                                        [label]="'RFP / RFQ'"
                                        [id]="'rfpRfqLine'"
                                        [placeholder]="'Search or select option...'"
                                        [disabled]="!isEditMode"
                                        formControlName="rfpRfq">
                                    </app-searchable-select>

                                    <app-searchable-select
                                        [options]="tenderClassifications"
                                        [label]="'Tender Classification'"
                                        [id]="'tenderClassificationLine'"
                                        [placeholder]="'Search or select classification...'"
                                        [disabled]="!isEditMode"
                                        formControlName="tenderClassification">
                                    </app-searchable-select>

                                    <div class="mb-3">
                                        <label for="companyLine" class="form-label fw-bold">Company</label>
                                        <input type="text" id="companyLine" class="form-control rounded-4 bg-light p-2"
                                            formControlName="company" [readonly]="!isEditMode">
                                    </div>

                                    <app-searchable-select
                                        [options]="euDepartments"
                                        [label]="'Department'"
                                        [id]="'euDepartmentLine'"
                                        [placeholder]="'Search or select department...'"
                                        [disabled]="!isEditMode"
                                        formControlName="euDepartment">
                                    </app-searchable-select>

                                    <app-searchable-select
                                        [options]="spocOptions"
                                        [label]="'Department SPOC'"
                                        [id]="'euDepartmentSpocLine'"
                                        [placeholder]="'Search or select SPOC...'"
                                        [disabled]="!isEditMode"
                                        formControlName="euDepartmentSpoc">
                                    </app-searchable-select>

                                    <app-searchable-select
                                        [options]="purchaseRequisitions"
                                        [label]="'Purchase requisition'"
                                        [id]="'purchaseRequisitionLine'"
                                        [placeholder]="'Search or select requisition...'"
                                        [disabled]="true"
                                        formControlName="purchaseRequisition">
                                    </app-searchable-select>

                                    <app-searchable-select
                                        [options]="spocOptions"
                                        [label]="'Managed by SPOC'"
                                        [id]="'managedBySpocLine'"
                                        [placeholder]="'Search or select SPOC...'"
                                        [disabled]="!isEditMode"
                                        formControlName="managedBySpoc">
                                    </app-searchable-select>
                                </div>

                                <!-- Middle Column -->
                                <div class="col-12 col-md-4">
                                    <div class="mb-3">
                                        <label for="managedByContactDetailsLine" class="form-label fw-bold">Managed by
                                            contact details (SPOC)</label>
                                        <textarea id="managedByContactDetailsLine"
                                            class="form-control rounded-4 bg-light p-2"
                                            formControlName="managedByContactDetails" rows="3"
                                            [readonly]="!isEditMode"></textarea>
                                    </div>

                                    <app-searchable-select
                                        [options]="currencies"
                                        [label]="'Currency'"
                                        [id]="'currencyLine'"
                                        [placeholder]="'Search or select currency...'"
                                        [disabled]="!isEditMode"
                                        formControlName="currency">
                                    </app-searchable-select>

                                    <div class="mb-3">
                                        <label for="tenderValueLine" class="form-label fw-bold">Tender Value</label>
                                        <input type="text" id="tenderValueLine"
                                            class="form-control rounded-4 bg-light p-2" formControlName="tenderValue"
                                            [readonly]="true">
                                    </div>

                                    <div class="mb-3">
                                        <label for="tenderValueInQARLine" class="form-label fw-bold">Tender value in
                                            QAR</label>
                                        <input type="text" id="tenderValueInQARLine"
                                            class="form-control rounded-4 bg-light p-2"
                                            formControlName="tenderValueInQAR" [readonly]="true">
                                    </div>

                                    <div class="mb-3">
                                        <label for="awaitingValueLine" class="form-label fw-bold">Awaiting value</label>
                                        <input type="text" id="awaitingValueLine"
                                            class="form-control rounded-4 bg-light p-2" formControlName="awaitingValue"
                                            [readonly]="!isEditMode">
                                    </div>

                                    <div class="mb-3">
                                        <label for="awardingValueInQARLine" class="form-label fw-bold">Awarding Value in
                                            QAR</label>
                                        <input type="text" id="awardingValueInQARLine"
                                            class="form-control rounded-4 bg-light p-2"
                                            formControlName="awardingValueInQAR" [readonly]="!isEditMode">
                                    </div>

                                    <div class="mb-3">
                                        <label for="startDateOfTenderLine" class="form-label fw-bold">Start date of
                                            tender</label>
                                        <input type="date" id="startDateOfTenderLine"
                                            class="form-control rounded-4 bg-light p-2"
                                            formControlName="startDateOfTender" [readonly]="!isEditMode">
                                    </div>

                                    <div class="mb-3">
                                        <label for="acceptanceDeadlineLine" class="form-label fw-bold">Acceptance
                                            deadline</label>
                                        <input type="date" id="acceptanceDeadlineLine"
                                            class="form-control rounded-4 bg-light p-2"
                                            formControlName="acceptanceDeadline" [readonly]="!isEditMode">
                                    </div>

                                    <div class="mb-3">
                                        <label for="extendedAcceptanceDeadlineLine" class="form-label fw-bold">Extended
                                            acceptance deadline</label>
                                        <input type="date" id="extendedAcceptanceDeadlineLine"
                                            class="form-control rounded-4 bg-light p-2"
                                            formControlName="extendedAcceptanceDeadline" [readonly]="!isEditMode">
                                    </div>
                                </div>

                                <!-- Right Column -->
                                <div class="col-12 col-md-4">
                                    <div class="mb-3">
                                        <label for="noteLine" class="form-label fw-bold">Note</label>
                                        <textarea id="noteLine" class="form-control rounded-4 bg-light p-2"
                                            formControlName="note" rows="3" [readonly]="!isEditMode"></textarea>
                                    </div>

                                    <div class="mb-3">
                                        <label for="recommendationJustificationLine"
                                            class="form-label fw-bold">Recommendation / justification</label>
                                        <textarea id="recommendationJustificationLine"
                                            class="form-control rounded-4 bg-light p-2"
                                            formControlName="recommendationJustification" rows="3"
                                            [readonly]="true"></textarea>
                                    </div>

                                    <!-- <div class="mb-3">
                                        <label class="form-label fw-bold">Is joining fee required</label>
                                        <div class="d-flex gap-3">
                                            <div class="form-check">
                                                <input class="form-check-input" type="radio"
                                                    name="isJoiningFeeRequiredLine" id="joiningFeeYesLine" value="Yes"
                                                    formControlName="isJoiningFeeRequired" [disabled]="!isEditMode">
                                                <label class="form-check-label" for="joiningFeeYesLine">Yes</label>
                                            </div>
                                            <div class="form-check">
                                                <input class="form-check-input" type="radio"
                                                    name="isJoiningFeeRequiredLine" id="joiningFeeNoLine" value="No"
                                                    formControlName="isJoiningFeeRequired" [disabled]="!isEditMode">
                                                <label class="form-check-label" for="joiningFeeNoLine">No</label>
                                            </div>
                                        </div>
                                    </div> -->

                                    <!-- <div class="mb-3">
                                        <label for="joiningFeeLine" class="form-label fw-bold">Joining fee</label>
                                        <input type="text" id="joiningFeeLine"
                                            class="form-control rounded-4 bg-light p-2" formControlName="joiningFee"
                                            [readonly]="!isEditMode">
                                    </div> -->

                                    <div class="mb-3">
                                        <label for="bidClosureDateLine" class="form-label fw-bold">Bid closure
                                            date</label>
                                        <input type="date" id="bidClosureDateLine"
                                            class="form-control rounded-4 bg-light p-2" formControlName="bidClosureDate"
                                            [readonly]="!isEditMode">
                                    </div>
                                </div>
                            </div>
                        </fieldset>

                        <!-- Selected vendor Section - Only show if vendors exist -->
                        <fieldset class="content-fieldset mb-4" *ngIf="isPrivateTender">
                            <legend class="section-legend">Selected vendor</legend>

                            <div class="d-flex gap-2 mb-3" *ngIf="isEditMode">
                                <button type="button" class="btn action-btn" (click)="addSelectedVendor()">
                                    + Add
                                </button>
                                <button
                                    type="button"
                                    class="btn action-btn"
                                    (click)="removeAllSelectedVendors()"
                                    [disabled]="selectedVendorsFormArray.length === 0">
                                    <i class="fas fa-trash"></i> Remove
                                </button>
                            </div>

                            <div class="table-responsive">
                                <table class="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Vendor account</th>
                                            <th>Name</th>
                                            <th *ngIf="isEditMode"></th>
                                        </tr>
                                    </thead>
                                    <tbody formArrayName="selectedVendors">
                                        <tr *ngFor="let vendor of selectedVendorsFormArray.controls; let i = index"
                                            [formGroupName]="i">
                                            <td>
                                                <app-searchable-select
                                                    *ngIf="isEditMode"
                                                    [options]="vendorAccounts"
                                                    [id]="'vendorAccount_' + i"
                                                    [placeholder]="'Search or select vendor account...'"
                                                    [label]="''"
                                                    formControlName="vendorAccount">
                                                </app-searchable-select>
                                                <input
                                                    *ngIf="!isEditMode"
                                                    type="text"
                                                    class="form-control rounded-4 bg-light p-2"
                                                    formControlName="vendorAccount"
                                                    readonly>
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    class="form-control rounded-4 bg-light p-2"
                                                    formControlName="vendorName"
                                                    readonly>
                                            </td>
                                            <td class="text-center" *ngIf="isEditMode">
                                                <button
                                                    type="button"
                                                    class="btn action-btn btn-sm"
                                                    (click)="removeSelectedVendor(i)"
                                                    [disabled]="selectedVendorsFormArray.length === 0"
                                                    title="Remove">
                                                    <i class="fas fa-trash"></i> Remove
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </fieldset>

                        <!-- Evaluation Criteria Section -->
                        <fieldset class="content-fieldset mb-4">
                            <legend class="section-legend">Evaluation Criteria</legend>

                            <div class="mb-3">
                                <label for="backgroundAndSOW" class="form-label fw-bold">Background and SOW</label>
                                <textarea id="backgroundAndSOW" class="form-control rounded-4 bg-light p-2"
                                    formControlName="backgroundAndSOW" rows="5" [readonly]="!isEditMode">
                                </textarea>
                            </div>

                            <div class="mb-3">
                                <label for="technicalEvaluation" class="form-label fw-bold">Technical evaluation</label>
                                <textarea id="technicalEvaluation" class="form-control rounded-4 bg-light p-2"
                                    formControlName="technicalEvaluation" rows="5" [readonly]="!isEditMode">
                                </textarea>
                            </div>

                            <div class="mb-3">
                                <label for="commercialEvaluation" class="form-label fw-bold">Commercial
                                    evaluation</label>
                                <textarea id="commercialEvaluation" class="form-control rounded-4 bg-light p-2"
                                    formControlName="commercialEvaluation" rows="5" [readonly]="!isEditMode">
                                </textarea>
                            </div>
                        </fieldset>

                        <!-- EVALUATION CRITERIA Section - Same for both Tenders and Practices -->
                        <fieldset class="content-fieldset mb-4">
                            <legend class="section-legend">Evaluation Criteria</legend>

                            <app-evaluation-criteria
                                [initialData]="evaluationCriteriaData"
                                [readonly]="!isEditMode"
                                [isPRSelected]="false"
                                (dataChange)="onEvaluationCriteriaChange($event)">
                            </app-evaluation-criteria>
                        </fieldset>

                        <!-- Pre-Qualification Criteria / Instructions Section -->
                        <fieldset class="content-fieldset mb-4">
                            <legend class="section-legend">Pre-Qualification Criteria / Instructions</legend>

                            <div class="row mb-4">
                                <!-- Left Sub-section: Tender bond -->
                                <div class="col-12 col-md-6">
                                    <div class="mb-3">
                                        <label class="form-label fw-bold">Tender bond required</label>
                                        <div class="d-flex gap-3">
                                            <div class="form-check">
                                                <input class="form-check-input" type="radio" name="tenderBondRequired"
                                                    id="tenderBondYes" value="Yes" formControlName="tenderBondRequired"
                                                    [disabled]="!isEditMode">
                                                <label class="form-check-label" for="tenderBondYes">Yes</label>
                                            </div>
                                            <div class="form-check">
                                                <input class="form-check-input" type="radio" name="tenderBondRequired"
                                                    id="tenderBondNo" value="No" formControlName="tenderBondRequired"
                                                    [disabled]="!isEditMode">
                                                <label class="form-check-label" for="tenderBondNo">No</label>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="mb-3">
                                        <label for="tenderBondDetails" class="form-label fw-bold">Tender bond
                                            details</label>
                                        <textarea id="tenderBondDetails" class="form-control rounded-4 bg-light p-2"
                                            formControlName="tenderBondDetails" rows="4" [readonly]="!isEditMode">
                                        </textarea>
                                    </div>

                                    <div class="mb-3">
                                        <label for="tenderBondValidityDate" class="form-label fw-bold">Tender bond
                                            validity date</label>
                                        <input type="date" id="tenderBondValidityDate"
                                            class="form-control rounded-4 bg-light p-2"
                                            formControlName="tenderBondValidityDate" [readonly]="!isEditMode">
                                    </div>
                                </div>

                                <!-- Right Sub-section: Performance bond -->
                                <div class="col-12 col-md-6">
                                    <div class="mb-3">
                                        <label class="form-label fw-bold">Performance bond required</label>
                                        <div class="d-flex gap-3">
                                            <div class="form-check">
                                                <input class="form-check-input" type="radio"
                                                    name="performanceBondRequired" id="performanceBondYes" value="Yes"
                                                    formControlName="performanceBondRequired" [disabled]="!isEditMode">
                                                <label class="form-check-label" for="performanceBondYes">Yes</label>
                                            </div>
                                            <div class="form-check">
                                                <input class="form-check-input" type="radio"
                                                    name="performanceBondRequired" id="performanceBondNo" value="No"
                                                    formControlName="performanceBondRequired" [disabled]="!isEditMode">
                                                <label class="form-check-label" for="performanceBondNo">No</label>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="mb-3">
                                        <label for="performanceBondDetails" class="form-label fw-bold">Performance bond
                                            details</label>
                                        <textarea id="performanceBondDetails"
                                            class="form-control rounded-4 bg-light p-2"
                                            formControlName="performanceBondDetails" rows="4"
                                            [readonly]="!isEditMode">
                                        </textarea>
                                    </div>

                                    <div class="mb-3">
                                        <label for="performanceBondValidityDate" class="form-label fw-bold">Performance
                                            bond validity date</label>
                                        <input type="date" id="performanceBondValidityDate"
                                            class="form-control rounded-4 bg-light p-2"
                                            formControlName="performanceBondValidityDate" [readonly]="!isEditMode">
                                    </div>
                                </div>
                            </div>
                        </fieldset>

                        <!-- Received biddings Section -->
                        <fieldset class="content-fieldset received-biddings-section mt-5">
                            <legend class="section-legend">Received biddings</legend>
                            <div class="action-buttons-row received-bidding-actions mb-4">
                                <button type="button" class="btn action-btn" [disabled]="!selectedBiddingId || isSelectedBidLocked()" [class.active]="selectedBiddingId" (click)="navigateToBidDetails()">Details</button>
                                <!-- <button type="button" class="btn action-btn" [disabled]="!selectedBiddingId || isSelectedBidLocked()">Vendor details</button> -->
                                <!-- <button type="button" class="btn action-btn" [disabled]="!selectedBiddingId || isSelectedBidLocked()">Pre-Qualification criteria</button> -->
                                <button type="button" class="btn action-btn" [disabled]="!selectedBiddingId || isSelectedBidLocked() || !isBidSubmitted()" (click)="openBiddingDocumentsModal()">Bidding documents</button>
                                <!-- <button type="button" class="btn action-btn" [disabled]="!selectedBiddingId || isSelectedBidLocked()">Vendor documents</button> -->
                                <!-- <button type="button" class="btn action-btn" [disabled]="!selectedBiddingId || isSelectedBidLocked()">Chat</button> -->

                                <button type="button" class="btn action-btn" [disabled]="!selectedBiddingId || isSelectedBidLocked()" (click)="openExtendClosureDateModal()">Extend closure date</button>
                                <button type="button" class="btn action-btn" [disabled]="!selectedBiddingId || isSelectedBidLocked()" (click)="openAwardModal()">Award</button>
                                <button type="button" class="btn action-btn" [disabled]="!selectedBiddingId || isSelectedBidLocked()" (click)="openDeclineModal()">Decline</button>
                                <button type="button" class="btn action-btn" [disabled]="!selectedBiddingId || isSelectedBidLocked()" (click)="openWithdrawModal()">Withdraw</button>
                                <button type="button" class="btn action-btn" [disabled]="!selectedBiddingId || isSelectedBidLocked()" (click)="openAllowToParticipateModal()">Allow to participate</button>
                                <!-- <button type="button" class="btn action-btn" [disabled]="!selectedBiddingId || isSelectedBidLocked()">Payment verified</button> -->
                                <!-- <div class="btn-group">
                                    <button type="button" class="btn action-btn dropdown-toggle"
                                        data-bs-toggle="dropdown" [disabled]="!selectedBiddingId || isSelectedBidLocked()">Payment pending</button>
                                </div> -->
                            </div>

                            <!-- Biddings Table -->
                            <app-data-table
                                *ngIf="receivedBiddings.length > 0"
                                [data]="receivedBiddings"
                                [columns]="bidsTableColumns"
                                [paginator]="true"
                                [rows]="10"
                                [rowSelectionCallback]="isBidRowSelected"
                                (onRowClick)="onBidRowClick($event)"
                                (onLinkClick)="onBidLinkClick($event)"
                                (onActionClick)="onBidActionClick($event)">
                            </app-data-table>
                            <div *ngIf="receivedBiddings.length === 0" class="text-center p-4">
                                <p class="text-muted">No bids received for this tender yet.</p>
                            </div>
                        </fieldset>
                    </div>
                </form>
            </div>
        </div>

        <!-- Workflow Tab Content -->
        <div class="mandatory-application m-4" *ngIf="activeTab === 'Workflow'">
            <div class="inside-out p-4">
                <div class="workflow-dashboard">
                    <!-- Header Section -->
                    <div class="workflow-header">
                        <div class="workflow-header-content">
                            <div class="workflow-status-info">
                                <div class="status-title" *ngIf="workflowStatus">
                                    <div class="header-clock-icon">
                                        <i class="bi bi-diagram-3"></i>
                                    </div>
                                    <span>Workflow Status: <span class="status-badge-workflow" [style.background-color]="getWorkflowStatusColor(workflowStatus)">{{ workflowStatus }}</span></span>
                                </div>
                            </div>
                            <div class="workflow-action-panel" *ngIf="!isEditMode && !publishedBy && (canPerformWorkflowAction() || canRequestChangeWorkflowAction())">
                                <button *ngIf="canRequestChangeWorkflowAction()" type="button" class="btn workflow-action-btn" (click)="handleRequestChangeAction()">Request Change</button>
                                <button *ngIf="canPerformWorkflowAction()" type="button" class="btn workflow-action-btn" (click)="handleWorkflowAction()">{{ getWorkflowActionButtonText() }}</button>
                            </div>
                            <div class="expected-completion">
                            </div>
                        </div>
                    </div>

                    <!-- Tender Process Timeline Section -->
                    <fieldset class="content-fieldset mb-4">
                        <legend class="section-legend">Tender Process Timeline</legend>
                        <div class="tender-process-timeline">
                            <div *ngFor="let step of timelineSteps; let i = index" class="timeline-step"
                                 [ngClass]="{'active': step.status === 'active', 'completed': step.status === 'completed', 'future': step.status === 'future', 'pending': step.actionRequired}">
                                <div class="timeline-icon-wrapper">
                                    <div class="timeline-icon" [ngClass]="step.status" [class.pending]="step.actionRequired">
                                        <div *ngIf="step.status === 'completed'" class="timeline-icon-inner">
                                            <i class="bi bi-check"></i>
                                        </div>
                                        <i *ngIf="step.actionRequired && step.status !== 'completed'" class="bi bi-clock-history"></i>
                                        <i *ngIf="!step.actionRequired && step.status === 'active' && step.icon === 'users'" class="bi bi-people"></i>
                                        <i *ngIf="!step.actionRequired && step.status === 'future' && step.icon === 'shield'" class="bi bi-shield-check"></i>
                                        <i *ngIf="!step.actionRequired && step.status === 'future' && step.icon === 'document'" class="bi bi-file-earmark-text"></i>
                                    </div>
                                    <div class="timeline-connector" *ngIf="i < timelineSteps.length - 1"></div>
                                </div>
                                <div class="timeline-content">
                                    <div class="timeline-stage-number" *ngIf="step.stageNumber !== undefined && step.stageNumber !== null">Stage {{ step.stageNumber }}</div>
                                    <div class="timeline-step-name" *ngIf="step.stepName">{{ step.stepName }}</div>
                                    <div class="timeline-step-title">
                                        <span *ngIf="step.actionRequired && step.assignedTo && step.assignedTo.length > 0">Pending on {{ step.assignedTo }}</span>
                                        <span *ngIf="step.actionRequired && (!step.assignedTo || step.assignedTo.length === 0)">{{ step.title }}</span>
                                        <span *ngIf="!step.actionRequired">{{ step.title }}</span>
                                    </div>
                                    <div class="timeline-step-responsible" *ngIf="step.responsibleRole">Responsible: {{ step.responsibleRole }}</div>
                                    <div *ngIf="step.actionRequired" class="action-required">
                                        <div class="action-required-icon">
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="8" cy="8" r="8" fill="#DC3545"/>
                                                <path d="M8 4.5V8M8 11.5H8.01" stroke="white" stroke-width="1.2" stroke-linecap="round"/>
                                                <circle cx="8" cy="11.5" r="0.5" fill="white"/>
                                            </svg>
                                        </div>
                                        <span>Action Required: {{ step.actionRequired }}</span>
                                    </div>
                                    <div class="timeline-step-date" *ngIf="step.date">{{ step.date }}</div>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    <!-- Workflow History Section -->
                    <fieldset class="content-fieldset mb-4" *ngIf="workflowHistory && workflowHistory.length > 0">
                        <legend class="section-legend">Workflow History</legend>
                        <div class="workflow-history-table">
                            <table class="table table-striped table-hover">
                                <thead>
                                    <tr>
                                        <th>Action</th>
                                        <th>From Status</th>
                                        <th>To Status</th>
                                        <th>Action By</th>
                                        <th>Date & Time</th>
                                        <th>Comments</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngFor="let history of workflowHistory">
                                        <td><span class="badge badge-primary">{{ history.action }}</span></td>
                                        <td>{{ formatWorkflowStatus(history.fromStatus) }}</td>
                                        <td>{{ formatWorkflowStatus(history.toStatus) }}</td>
                                        <td>{{ history.actionBy || 'System' }}</td>
                                        <td>{{ formatDateTime(history.actionDate) }}</td>
                                        <td>{{ history.comments || '-' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </fieldset>
                </div>
            </div>
        </div>

        <!-- Documents Tab Content -->
        <div class="mandatory-application m-4" *ngIf="activeTab === 'Documents'">
            <div class="inside-out p-4">
                <div class="mandatory-fields"></div>
                <div class="container pt-4">
                    <!-- Tender Documents Section -->
                    <fieldset class="content-fieldset mb-4">
                        <legend class="section-legend">Tender Documents</legend>

                        <div *ngIf="isLoadingDocuments" class="text-center p-4">
                            <p class="text-muted">Loading documents...</p>
                        </div>

                        <div class="d-flex gap-2 mb-3" *ngIf="isEditMode">
                            <button type="button" class="btn action-btn" (click)="addTenderDocument()">
                                + Add Document
                            </button>
                        </div>

                        <div *ngIf="!isLoadingDocuments && tenderDocuments.length === 0" class="alert alert-info">
                            No tender documents found.
                        </div>

                        <div *ngIf="!isLoadingDocuments && tenderDocuments.length > 0" class="table-responsive">
                            <table class="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Document Name</th>
                                        <th>File</th>
                                        <th>Additional Details</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngFor="let doc of tenderDocuments; let i = index">
                                        <td>
                                            <input
                                                *ngIf="isEditMode && doc.isNew"
                                                type="text"
                                                class="form-control rounded-4 bg-light p-2"
                                                [(ngModel)]="doc.name"
                                                placeholder="Enter document name">
                                            <span *ngIf="!isEditMode || !doc.isNew">{{ doc.name }}</span>
                                        </td>
                                        <td>
                                            <input
                                                *ngIf="isEditMode && doc.isNew"
                                                type="file"
                                                class="form-control rounded-4 bg-light p-2"
                                                (change)="onTenderDocumentFileSelected($event, i)"
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png">
                                            <span *ngIf="!doc.isNew">Current file</span>
                                            <small class="text-muted d-block" *ngIf="doc.isNew && doc.fileContent">Selected: {{ doc.name }}</small>
                                        </td>
                                        <td>
                                            <textarea
                                                *ngIf="isEditMode && doc.isNew"
                                                class="form-control rounded-4 bg-light p-2"
                                                [(ngModel)]="doc.additionalDetails"
                                                rows="2"
                                                placeholder="Enter additional details"></textarea>
                                            <span *ngIf="!isEditMode || !doc.isNew">{{ doc.additionalDetails || '-' }}</span>
                                        </td>
                                        <td>
                                            <div class="btn-group" role="group">
                                                <button
                                                    *ngIf="!doc.isNew"
                                                    type="button"
                                                    class="btn action-btn btn-sm"
                                                    (click)="viewFile(doc.fileContent, doc.name, doc.mimeType)"
                                                    title="View">
                                                    <i class="fas fa-eye"></i> View
                                                </button>
                                                <button
                                                    *ngIf="!doc.isNew"
                                                    type="button"
                                                    class="btn action-btn btn-sm"
                                                    (click)="downloadFile(doc.fileContent, doc.name, doc.mimeType)"
                                                    title="Download">
                                                    <i class="fas fa-download"></i> Download
                                                </button>
                                                <button
                                                    *ngIf="isEditMode"
                                                    type="button"
                                                    class="btn action-btn btn-sm"
                                                    (click)="removeTenderDocument(i)"
                                                    title="Remove">
                                                    <i class="fas fa-trash"></i> Remove
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </fieldset>
                </div>
            </div>
        </div>
    </div>

    <!-- Confirmation Dialog -->
    <div class="confirmation-dialog-overlay" *ngIf="showConfirmDialog" (click)="cancelWorkflowAction()">
        <div class="confirmation-dialog" (click)="$event.stopPropagation()">
            <div class="dialog-content">
                <p>{{ getConfirmDialogMessage() }}</p>
                <div class="mb-3" *ngIf="pendingAction === 'Request Change'">
                    <label class="form-label fw-bold" for="workflowChangeComments">Comments</label>
                    <textarea id="workflowChangeComments" class="form-control" rows="3" [(ngModel)]="workflowComments" [ngModelOptions]="{standalone: true}" placeholder="Enter change request comments"></textarea>
                </div>
            </div>
            <div class="dialog-footer">
                <button class="btn btn-secondary" (click)="cancelWorkflowAction()">Cancel</button>
                <button class="btn action-btn-purple" (click)="confirmWorkflowAction()">{{ pendingAction || workflowAction }}</button>
            </div>
        </div>
    </div>

    <!-- Extend Closure Date Dialog -->
    <div class="confirmation-dialog-overlay" *ngIf="showExtendClosureDateModal" (click)="closeExtendClosureDateModal()">
        <div class="confirmation-dialog extend-date-dialog" (click)="$event.stopPropagation()">
            <div class="dialog-content">
                <h5 class="dialog-title mb-3">Extend Closure Date</h5>
                <p class="mb-3">Select a new closure date for bid: <strong>{{ selectedBiddingId }}</strong></p>
                <div class="mb-3">
                    <label for="extendedClosureDate" class="form-label fw-bold">New Closure Date</label>
                    <input
                        type="date"
                        id="extendedClosureDate"
                        class="form-control rounded-4 p-2"
                        [(ngModel)]="extendedClosureDate"
                        [min]="minDate"
                        required>
                </div>
            </div>
            <div class="dialog-footer">
                <button class="btn btn-secondary" (click)="closeExtendClosureDateModal()">Cancel</button>
                <button class="btn action-btn-purple" (click)="confirmExtendClosureDate()" [disabled]="!extendedClosureDate">OK</button>
            </div>
        </div>
    </div>

    <!-- Allow to Participate Confirmation Dialog -->
    <div class="confirmation-dialog-overlay" *ngIf="showAllowToParticipateModal" (click)="closeAllowToParticipateModal()">
        <div class="confirmation-dialog" (click)="$event.stopPropagation()">
            <div class="dialog-content">
                <h5 class="dialog-title mb-3">Allow to Participate</h5>
                <p class="mb-3">Are you sure you want to allow bid <strong>{{ selectedBiddingId }}</strong> to participate? This will change the status to Draft.</p>
            </div>
            <div class="dialog-footer">
                <button class="btn btn-secondary" (click)="closeAllowToParticipateModal()">Cancel</button>
                <button class="btn action-btn-purple" (click)="confirmAllowToParticipate()">Confirm</button>
            </div>
        </div>
    </div>

    <!-- Withdraw Confirmation Dialog -->
    <div class="confirmation-dialog-overlay" *ngIf="showWithdrawModal" (click)="closeWithdrawModal()">
        <div class="confirmation-dialog" (click)="$event.stopPropagation()">
            <div class="dialog-content">
                <h5 class="dialog-title mb-3">Withdraw Bid</h5>
                <p class="mb-3">Are you sure you want to withdraw bid <strong>{{ selectedBiddingId }}</strong>? This will change the status to Withdrawn.</p>
            </div>
            <div class="dialog-footer">
                <button class="btn btn-secondary" (click)="closeWithdrawModal()">Cancel</button>
                <button class="btn action-btn-purple" (click)="confirmWithdraw()">Confirm</button>
            </div>
        </div>
    </div>

    <!-- Decline Confirmation Dialog with File Upload -->
    <div class="confirmation-dialog-overlay" *ngIf="showDeclineModal" (click)="closeDeclineModal()">
        <div class="confirmation-dialog" (click)="$event.stopPropagation()">
            <div class="dialog-content">
                <h5 class="dialog-title mb-3">Decline Bid</h5>
                <p class="mb-3">Are you sure you want to decline bid <strong>{{ selectedBiddingId }}</strong>? Please upload the regret letter.</p>
                <div class="mb-3">
                    <label for="declineRegretLetter" class="form-label fw-bold">Regret Letter <span class="text-danger">*</span></label>
                    <input
                        type="file"
                        id="declineRegretLetter"
                        class="form-control rounded-4 p-2"
                        accept=".pdf,application/pdf"
                        (change)="onDeclineFileSelected($event)"
                        required>
                    <small class="text-muted">Accepted format: PDF (maximum 5 MB)</small>
                </div>
            </div>
            <div class="dialog-footer">
                <button class="btn btn-secondary" (click)="closeDeclineModal()">Cancel</button>
                <button class="btn action-btn-purple" (click)="confirmDecline()" [disabled]="!declineRegretLetterFile">Confirm</button>
            </div>
        </div>
    </div>

    <!-- Award Confirmation Dialog with File Upload -->
    <div class="confirmation-dialog-overlay" *ngIf="showAwardModal" (click)="closeAwardModal()">
        <div class="confirmation-dialog" (click)="$event.stopPropagation()">
            <div class="dialog-content">
                <h5 class="dialog-title mb-3">Award Bid</h5>
                <p class="mb-3">Are you sure you want to award bid <strong>{{ selectedBiddingId }}</strong>? This will regret all other bids in this {{ isPractice ? 'practice' : 'tender' }}. Please upload the award letter.</p>
                <div class="mb-3">
                    <label for="awardLetter" class="form-label fw-bold">Award Letter <span class="text-danger">*</span></label>
                    <input
                        type="file"
                        id="awardLetter"
                        class="form-control rounded-4 p-2"
                        accept=".pdf,application/pdf"
                        (change)="onAwardFileSelected($event)"
                        required>
                    <small class="text-muted">Accepted format: PDF (maximum 5 MB)</small>
                </div>
                <div class="mb-3">
                    <label for="awardRegretLetter" class="form-label fw-bold">Regret Letter <span class="text-muted">(optional)</span></label>
                    <input
                        type="file"
                        id="awardRegretLetter"
                        class="form-control rounded-4 p-2"
                        accept=".pdf,application/pdf"
                        (change)="onAwardRegretFileSelected($event)">
                    <small class="text-muted">When provided, this PDF is sent to the vendors whose submitted bids are regretted by this award.</small>
                </div>
            </div>
            <div class="dialog-footer">
                <button class="btn btn-secondary" (click)="closeAwardModal()">Cancel</button>
                <button class="btn action-btn-purple" (click)="confirmAward()" [disabled]="!awardLetterFile">Award Bid</button>
            </div>
        </div>
    </div>

    <!-- Bidding Documents Dialog -->
    <div class="confirmation-dialog-overlay" *ngIf="showBiddingDocumentsModal" (click)="closeBiddingDocumentsModal()">
        <div class="bidding-documents-dialog" (click)="$event.stopPropagation()">
            <div class="dialog-header">
                <h5 class="dialog-title mb-0">Bidding Documents - {{ selectedBiddingId }}</h5>
                <button type="button" class="btn-close" (click)="closeBiddingDocumentsModal()" aria-label="Close"></button>
            </div>
            <div class="dialog-content" style="max-height: 70vh; overflow-y: auto;">
                <div *ngIf="isLoadingBiddingDocuments" class="text-center p-4">
                    <p class="text-muted">Loading documents...</p>
                </div>

                <div *ngIf="!isLoadingBiddingDocuments" class="documents-section">
                    <div class="document-category">
                        <h5 class="document-category-title">Commercial proposal*</h5>
                        <div *ngIf="biddingDocuments.commercialProposal.length === 0" class="no-documents p-3">
                            <p class="text-muted mb-0">No documents uploaded</p>
                        </div>
                        <div class="document-item d-flex justify-content-between align-items-center p-3 mb-3" *ngFor="let doc of biddingDocuments.commercialProposal">
                            <div class="d-flex align-items-center flex-grow-1">
                                <div class="document-icon me-3"><i class="bi bi-file-earmark-text"></i></div>
                                <div class="document-info flex-grow-1" *ngIf="!isBiddingDocumentSealed(doc); else sealedCommercialDocument">
                                    <div class="document-name fw-bold mb-1">{{ getBiddingDocumentName(doc) }}</div>
                                    <div class="document-meta">
                                        <span class="document-size light-text-m">{{ formatFileSize(getBiddingDocumentSize(doc)) }}</span>
                                        <span class="document-date light-text-m" *ngIf="getBiddingDocumentDate(doc)">Uploaded {{ getBiddingDocumentDate(doc) | date: 'yyyy-MM-dd' }}</span>
                                        <span class="document-date light-text-m" *ngIf="getBiddingDocumentExpiryDate(doc)">Expires {{ getBiddingDocumentExpiryDate(doc) | date: 'yyyy-MM-dd' }}</span>
                                    </div>
                                </div>
                                <ng-template #sealedCommercialDocument>
                                    <div class="document-info sealed-document-info flex-grow-1">
                                        <div class="document-name fw-bold mb-1">Document sealed</div>
                                        <div class="document-meta" *ngIf="getBiddingDocumentExpiryDate(doc)">
                                            <span class="document-date light-text-m">Expires {{ getBiddingDocumentExpiryDate(doc) | date: 'yyyy-MM-dd' }}</span>
                                        </div>
                                    </div>
                                </ng-template>
                            </div>
                            <button *ngIf="canPreviewBiddingDocument(doc)" class="btn download-btn" (click)="previewDocument(getBiddingDocumentAttachmentId(doc), getBiddingDocumentName(doc))">Preview</button>
                        </div>
                    </div>

                    <div class="document-category">
                        <h5 class="document-category-title">Technical proposal*</h5>
                        <div *ngIf="biddingDocuments.technicalProposal.length === 0" class="no-documents p-3">
                            <p class="text-muted mb-0">No documents uploaded</p>
                        </div>
                        <div class="document-item d-flex justify-content-between align-items-center p-3 mb-3" *ngFor="let doc of biddingDocuments.technicalProposal">
                            <div class="d-flex align-items-center flex-grow-1">
                                <div class="document-icon me-3"><i class="bi bi-file-earmark-text"></i></div>
                                <div class="document-info flex-grow-1" *ngIf="!isBiddingDocumentSealed(doc); else sealedTechnicalDocument">
                                    <div class="document-name fw-bold mb-1">{{ getBiddingDocumentName(doc) }}</div>
                                    <div class="document-meta">
                                        <span class="document-size light-text-m">{{ formatFileSize(getBiddingDocumentSize(doc)) }}</span>
                                        <span class="document-date light-text-m" *ngIf="getBiddingDocumentDate(doc)">Uploaded {{ getBiddingDocumentDate(doc) | date: 'yyyy-MM-dd' }}</span>
                                        <span class="document-date light-text-m" *ngIf="getBiddingDocumentExpiryDate(doc)">Expires {{ getBiddingDocumentExpiryDate(doc) | date: 'yyyy-MM-dd' }}</span>
                                    </div>
                                </div>
                                <ng-template #sealedTechnicalDocument>
                                    <div class="document-info sealed-document-info flex-grow-1">
                                        <div class="document-name fw-bold mb-1">Document sealed</div>
                                        <div class="document-meta" *ngIf="getBiddingDocumentExpiryDate(doc)">
                                            <span class="document-date light-text-m">Expires {{ getBiddingDocumentExpiryDate(doc) | date: 'yyyy-MM-dd' }}</span>
                                        </div>
                                    </div>
                                </ng-template>
                            </div>
                            <button *ngIf="canPreviewBiddingDocument(doc)" class="btn download-btn" (click)="previewDocument(getBiddingDocumentAttachmentId(doc), getBiddingDocumentName(doc))">Preview</button>
                        </div>
                    </div>

                    <div class="document-category" *ngFor="let category of [
                        { title: 'Tender bond', docs: biddingDocuments.tenderBond },
                        { title: 'Performance Bond', docs: biddingDocuments.performanceBond },
                        { title: 'Other documents', docs: biddingDocuments.otherDocuments }
                    ]">
                        <h5 class="document-category-title">{{ category.title }}</h5>
                        <div *ngIf="category.docs.length === 0" class="no-documents p-3">
                            <p class="text-muted mb-0">No documents uploaded</p>
                        </div>
                        <div class="document-item d-flex justify-content-between align-items-center p-3 mb-3" *ngFor="let doc of category.docs">
                            <div class="d-flex align-items-center flex-grow-1">
                                <div class="document-icon me-3"><i class="bi bi-file-earmark-text"></i></div>
                                <div class="document-info flex-grow-1">
                                    <div class="document-name fw-bold mb-1">{{ getBiddingDocumentName(doc) }}</div>
                                    <div class="document-meta">
                                        <span class="document-size light-text-m">{{ formatFileSize(getBiddingDocumentSize(doc)) }}</span>
                                        <span class="document-date light-text-m" *ngIf="getBiddingDocumentDate(doc)">Uploaded {{ getBiddingDocumentDate(doc) | date: 'yyyy-MM-dd' }}</span>
                                        <span class="document-date light-text-m" *ngIf="getBiddingDocumentExpiryDate(doc)">Expires {{ getBiddingDocumentExpiryDate(doc) | date: 'yyyy-MM-dd' }}</span>
                                    </div>
                                </div>
                            </div>
                            <button *ngIf="canPreviewBiddingDocument(doc)" class="btn download-btn" (click)="previewDocument(getBiddingDocumentAttachmentId(doc), getBiddingDocumentName(doc))">Preview</button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="dialog-footer">
                <button class="btn btn-secondary" (click)="closeBiddingDocumentsModal()">Close</button>
            </div>
        </div>
    </div>
</div>
